import React from 'react';

/**
 * Global widget styles for EKKO application
 * Providing consistent font sizing and color improvements across widgets
 */

// Global styles for consistent font sizing across all widgets
export const globalWidgetTextStyles = `
  /* CSS custom properties for standardized text sizes */
  :root {
    --text-xs: 14px;
    --text-sm: 16px;
    --text-base: 18px;
    --text-lg: 20px;
    --text-xl: 24px;
    --text-2xl: 30px;
    --text-3xl: 36px;
    --text-4xl: 48px;
  }

  /* Standardize all small font sizes to readable minimum of 14px */
  .ekko-widget .text-\[8px\], 
  .ekko-widget .text-\[9px\],
  .ekko-widget .text-\[10px\],
  .ekko-widget .text-\[11px\],
  .ekko-widget .text-\[12px\] {
    font-size: var(--text-xs) !important; /* 14px - minimum readable size */
  }
  
  /* AGGRESSIVE TEXT COLOR OVERRIDES - Catch ALL light text */
  /* NO WHITE TEXT ON LIGHT BACKGROUNDS */
  .ekko-widget .text-white {
    color: #111827 !important; /* Convert white to very dark grey */
  }
  
  .ekko-widget .text-gray-50 {
    color: #111827 !important; /* Very dark grey */
  }
  
  .ekko-widget .text-gray-100 {
    color: #1f2937 !important; /* Dark grey */
  }
  
  .ekko-widget .text-gray-200 {
    color: #374151 !important; /* Medium dark grey */
  }
  
  .ekko-widget .text-gray-300 {
    color: #1f2937 !important; /* MUCH DARKER - was too light */
  }
  
  .ekko-widget .text-gray-400 {
    color: #111827 !important; /* VERY DARK - was too light */
  }
  
  .ekko-widget .text-gray-500 {
    color: #0f172a !important; /* ALMOST BLACK for maximum visibility */
  }
  
  .ekko-widget .text-gray-600 {
    color: #0f172a !important; /* Almost black for maximum contrast */
  }
  
  .ekko-widget .text-gray-700 {
    color: #0f172a !important; /* Very dark */
  }
  
  .ekko-widget .text-gray-800 {
    color: #0f172a !important; /* Very dark */
  }
  
  .ekko-widget .text-gray-900 {
    color: #0f172a !important; /* Very dark */
  }

  /* CATCH ALL REMAINING GREY TEXT - Most aggressive override */
  .ekko-widget [class*="text-gray-"] {
    color: #1f2937 !important; /* Force all grey text to be dark */
  }
  
  /* SPECIFIC OVERRIDES for really stubborn cases */
  .ekko-widget .text-\[10px\].text-gray-300,
  .ekko-widget .text-\[10px\].text-gray-400,
  .ekko-widget .text-\[9px\].text-gray-300,
  .ekko-widget .text-\[9px\].text-gray-400,
  .ekko-widget .text-\[9px\].text-gray-500,
  .ekko-widget .text-\[9px\].text-gray-600 {
    color: #111827 !important; /* Force small grey text to be very dark */
  }
  
  /* Special handling for text on dark elements - only apply dark text to elements with light backgrounds */
  .ekko-widget .bg-black .text-white,
  .ekko-widget .bg-gray-900 .text-white,
  .ekko-widget .bg-gray-800 .text-white,
  .ekko-widget [style*="background-color: rgb(22, 22, 26)"] .text-white,
  .ekko-widget [style*="background-color: #16161a"] .text-white {
    color: #f3f4f6 !important; /* Keep white text on dark backgrounds */
  }
  
  /* Update Tailwind class overrides to use standardized sizes */
  .ekko-widget .text-xs {
    font-size: var(--text-xs) !important; /* 14px */
  }
  
  .ekko-widget .text-sm {
    font-size: var(--text-sm) !important; /* 16px */
  }
  
  .ekko-widget .text-base {
    font-size: var(--text-base) !important; /* 18px */
  }
  
  .ekko-widget .text-lg {
    font-size: var(--text-lg) !important; /* 20px */
  }
  
  /* Standardized headers with proper line-height for alignment */
  .ekko-widget h1 { 
    font-size: var(--text-4xl) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  .ekko-widget h2 { 
    font-size: var(--text-3xl) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  .ekko-widget h3 { 
    font-size: var(--text-2xl) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  .ekko-widget h4 { 
    font-size: var(--text-xl) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  .ekko-widget h5 { 
    font-size: var(--text-lg) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  .ekko-widget h6 { 
    font-size: var(--text-base) !important; 
    line-height: 1.2 !important;
    color: #111827 !important; /* Dark headers */
  }
  
  /* Standardize main text elements to consistent readable size and dark color */
  .ekko-widget p, 
  .ekko-widget div, 
  .ekko-widget span,
  .ekko-widget button,
  .ekko-widget label,
  .ekko-widget input,
  .ekko-widget select,
  .ekko-widget textarea {
    font-size: var(--text-sm) !important; /* 16px - good base size */
    line-height: 1.4 !important;
  }

  /* Make sure buttons have dark text for visibility */
  .ekko-widget button:not(.btn-primary):not([class*="bg-green"]):not([class*="bg-blue"]):not([class*="bg-red"]) {
    color: #374151 !important;
  }

  /* Fix for widget headers - ensure proper vertical alignment */
  .ekko-widget .flex.items-center h1,
  .ekko-widget .flex.items-center h2,
  .ekko-widget .flex.items-center h3,
  .ekko-widget .flex.items-center h4,
  .ekko-widget .flex.items-center h5,
  .ekko-widget .flex.items-center h6 {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    vertical-align: baseline !important;
  }

  /* Additional specific targeting for widget title containers */
  .ekko-widget [data-widget-type] .flex.items-center h1,
  .ekko-widget [data-widget-type] .flex.items-center h2,
  .ekko-widget [data-widget-type] .flex.items-center h3,
  .ekko-widget [data-widget-type] .flex.items-center h4,
  .ekko-widget [data-widget-type] .flex.items-center h5,
  .ekko-widget [data-widget-type] .flex.items-center h6 {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    vertical-align: baseline !important;
    display: inline-block !important;
  }

  /* Global widget title alignment fix */
  div[data-widget-type] h1,
  div[data-widget-type] h2,
  div[data-widget-type] h3,
  div[data-widget-type] h4,
  div[data-widget-type] h5,
  div[data-widget-type] h6,
  .widget-header h1,
  .widget-header h2,
  .widget-header h3,
  .widget-header h4,
  .widget-header h5,
  .widget-header h6 {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    vertical-align: baseline !important;
    display: inline-block !important;
  }

  /* Exception for icons or places where smaller text is truly necessary */
  .ekko-widget svg + span,
  .ekko-widget .text-xxs-exception {
    font-size: var(--text-xs) !important; /* 14px - still readable */
    line-height: 1.4 !important;
  }

  /* Scrollbars */
  .ekko-widget *::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  .ekko-widget *::-webkit-scrollbar-track {
    background: transparent;
  }

  .ekko-widget *::-webkit-scrollbar-thumb {
    background-color: rgba(75, 85, 99, 0.3);
    border-radius: 2px;
  }

  /* Custom scrollbar for all scrollable containers using .custom-scrollbar */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #23232b;
    border-radius: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #23232b transparent;
  }

  /* Hero-style button classes - now use the main index.css button system */
  /* The .btn-primary, .btn-outline, .btn-secondary classes are defined in index.css */
  /* This ensures consistency with the homepage hero buttons */
  
  .hero-button-primary {
    /* Inherits from .btn-primary in index.css */
  }
  
  .hero-button-secondary {
    /* Inherits from .btn-outline in index.css */
  }

  .btn-primary-small,
  .hero-button-primary-small {
    background-color: #24c35b !important;
    color: black !important;
    padding: 0.5rem 1.5rem !important;
    border-radius: 0.5rem !important;
    font-weight: 500 !important;
    transition: all 0.3s ease !important;
    border: none !important;
    cursor: pointer !important;
  }
  
  .btn-primary-small:hover,
  .hero-button-primary-small:hover {
    background-color: #1eaa4e !important;
  }
  
  .btn-secondary-small,
  .hero-button-secondary-small {
    padding: 0.5rem 1.5rem !important;
    background-color: #141418 !important;
    color: white !important;
    border: 1px solid #374151 !important;
    border-radius: 0.5rem !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
    cursor: pointer !important;
  }
  
  .btn-secondary-small:hover,
  .hero-button-secondary-small:hover {
    background-color: #1a1a1e !important;
  }
`;

// EKKO color theme variables - updated with improved text colors
export const ekkoColors = {
  primary: '#24c35b',
  primaryHover: '#1eaa4e',
  primaryActive: '#188f42',
  primaryLight: 'rgba(36, 195, 91, 0.1)',
  primaryBorder: 'rgba(36, 195, 91, 0.3)',
  darkBg: '#0d0d12',
  darkBgHover: '#16161e',
  darkBorder: 'rgba(75, 85, 99, 0.2)',
  darkBorderHover: 'rgba(75, 85, 99, 0.4)',
  textPrimary: '#111827', // Changed to dark text for light backgrounds
  textSecondary: '#374151', // Dark grey for good visibility
  textMuted: '#6b7280',     // Medium dark grey for better visibility
  gradientFrom: 'rgba(36, 195, 91, 0.05)',
  gradientTo: 'rgba(20, 20, 25, 0)',
  error: '#ef4444',
  warning: '#f59e0b',
  success: '#10b981',
};

// Common widget styles
export const globalWidgetStyles = `
  /* Common widget styling */
  .ekko-widget {
    background-color: ${ekkoColors.darkBg};
    border: 1px solid ${ekkoColors.darkBorder};
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .ekko-widget .widget-header {
    background: linear-gradient(to right, ${ekkoColors.darkBg}, ${ekkoColors.darkBgHover});
    border-bottom: 1px solid ${ekkoColors.darkBorder};
    padding: 0.5rem 1rem;
  }

  .ekko-widget .widget-body {
    background-color: ${ekkoColors.darkBg};
    padding: 1rem;
  }

  /* Buttons */
  .ekko-widget .primary-button {
    background-color: ${ekkoColors.primary};
    color: #000000;
    border-radius: 0.375rem;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .ekko-widget .primary-button:hover:not(:disabled) {
    background-color: ${ekkoColors.primaryHover};
  }

  .ekko-widget .primary-button:active:not(:disabled) {
    background-color: ${ekkoColors.primaryActive};
  }

  .ekko-widget .primary-button:disabled {
    background-color: ${ekkoColors.primaryLight};
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }

  .ekko-widget .secondary-button {
    background-color: transparent;
    color: ${ekkoColors.textPrimary};
    border: 1px solid ${ekkoColors.darkBorder};
    border-radius: 0.375rem;
    font-weight: 500;
    transition: border-color 0.2s, background-color 0.2s;
  }

  .ekko-widget .secondary-button:hover:not(:disabled) {
    border-color: ${ekkoColors.primaryBorder};
    background-color: rgba(36, 195, 91, 0.05);
  }

  /* Inputs */
  .ekko-widget input, .ekko-widget select, .ekko-widget textarea {
    background-color: rgba(15, 15, 20, 0.8);
    border: 1px solid ${ekkoColors.darkBorder};
    color: ${ekkoColors.textPrimary};
    border-radius: 0.375rem;
    transition: border-color 0.2s, background-color 0.2s;
  }

  .ekko-widget input:focus, .ekko-widget select:focus, .ekko-widget textarea:focus {
    border-color: ${ekkoColors.primaryBorder};
    outline: none;
    box-shadow: 0 0 0 1px rgba(36, 195, 91, 0.1);
    background-color: rgba(15, 15, 20, 0.9);
  }

  /* Placeholder styling */
  .ekko-widget input::placeholder, .ekko-widget textarea::placeholder {
    color: rgba(156, 163, 175, 0.5);
    opacity: 1;
  }

  .ekko-widget input::-webkit-input-placeholder, .ekko-widget textarea::-webkit-input-placeholder {
    color: rgba(156, 163, 175, 0.5);
  }

  .ekko-widget input::-moz-placeholder, .ekko-widget textarea::-moz-placeholder {
    color: rgba(156, 163, 175, 0.5);
    opacity: 1;
  }

  .ekko-widget input:-ms-input-placeholder, .ekko-widget textarea:-ms-input-placeholder {
    color: rgba(156, 163, 175, 0.5);
  }

  /* Remove spinner arrows completely */
  .ekko-widget input::-webkit-outer-spin-button,
  .ekko-widget input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    display: none;
  }

  /* For Firefox */
  .ekko-widget input[type=number] {
    -moz-appearance: textfield;
  }

  /* Enhanced Select/Dropdown styling */
  .ekko-widget select {
    background-color: rgba(15, 15, 20, 0.8);
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 16px;
    padding-right: 32px;
    cursor: pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  .ekko-widget select:hover {
    background-color: rgba(15, 15, 20, 0.9);
    border-color: ${ekkoColors.darkBorderHover};
  }

  .ekko-widget select:focus {
    background-color: rgba(15, 15, 20, 0.95);
  }

  /* Select option styling */
  .ekko-widget select option {
    background-color: rgba(15, 15, 20, 0.95);
    color: ${ekkoColors.textPrimary};
    padding: 8px 12px;
  }

  .ekko-widget select option:hover {
    background-color: rgba(30, 30, 35, 0.8);
  }

  .ekko-widget select option:checked,
  .ekko-widget select option:selected {
    background-color: rgba(36, 195, 91, 0.2);
    color: ${ekkoColors.primary};
  }

  /* Custom dropdown styling for better control */
  .ekko-widget .custom-select {
    position: relative;
    display: inline-block;
    width: 100%;
  }

  .ekko-widget .custom-select select {
    width: 100%;
  }

  .ekko-widget .custom-select::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 12px;
    width: 0;
    height: 0;
    border: 6px solid transparent;
    border-color: #666 transparent transparent transparent;
    transform: translateY(-50%);
    pointer-events: none;
  }

  /* Tables */
  .ekko-widget table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  .ekko-widget thead {
    background-color: rgba(15, 15, 20, 0.5);
  }

  .ekko-widget th {
    text-align: left;
    padding: 0.5rem;
    font-weight: 500;
    color: ${ekkoColors.textSecondary};
    border-bottom: 1px solid ${ekkoColors.darkBorder};
  }

  .ekko-widget td {
    padding: 0.5rem;
    border-bottom: 1px solid rgba(75, 85, 99, 0.1);
  }

  .ekko-widget tr:hover {
    background-color: rgba(36, 195, 91, 0.05);
  }

  /* Text colors */
  .ekko-widget .text-success {
    color: ${ekkoColors.primary};
  }

  .ekko-widget .text-error {
    color: ${ekkoColors.error};
  }

  .ekko-widget .text-warning {
    color: ${ekkoColors.warning};
  }
`;

// Function to inject global styles into a component
export const injectGlobalStyles = (additionalStyles = ''): React.ReactElement => {
  return React.createElement('style', null,
    globalWidgetTextStyles + globalWidgetStyles + additionalStyles
  );
};

/**
 * Apply this class to widget containers to enable global styling
 */
export const widgetClassName = 'ekko-widget'; 